// The in-game HUD (Mockup 1): a full-screen board with floating gilt panels — top bar, your dossier
// (left), rivals (top), the Gazette and Ledger (right) and the hand tray (bottom). The surface stays clean; detail lives one
// layer deep in tooltips (`data-tip`) and click feedback (`hint`). FX anchors (`data-anchor`) are
// the Director's flight targets; keep them when restyling.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { COLORS, statValue, type AvailableActions, type CardId, type Condition, type GameState, type LocationId, type Payment, type PlayerView } from '@bcm/rules';
import { QUICK_CHAT } from '@bcm/protocol';
import { useGame } from '../state/store';
import { C, COLOR_NAME, SEAL_COLORS, closingHour, defName, errorText, isAnnexRoom, lastRound, locationCapacity, opensRoom, pack, resolutionInForce, roundHour, values } from '../game/content';
import { LOCATION_ICON, SCHOOL_ICON, resolutionIcon, type IconName } from '../game/icons';
import { Card, CardBack, Glyph, RichText, tapPreview, usePreview } from './Card';
import { Icon, ManaCrystal, MeepleIcon, PermitToken, SeatEmblem } from './Icon';
import { GameLog } from './Overlays';
import { TOUCH, hint, pointer } from './Tooltip';
import { WaxSealArt } from './WaxSeal';
import { audio } from '../audio/sfx';

const RANKS = pack.ranks.map((r) => r.title);
const xy = (e: React.MouseEvent | { x: number; y: number }) => ('clientX' in e ? { x: e.clientX, y: e.clientY } : e);
const TOKEN: Record<string, string> = { red: '{R}', yellow: '{Y}', blue: '{B}', green: '{G}' };

// ─── Portrait ────────────────────────────────────────────────────────────────

const portraitCache = new Map<string, boolean>();

/** Identity portrait (a rendered bust when one exists) in a gilt medallion; the seat emblem otherwise. */
export function Portrait({ view, seat, size = 64 }: { view: PlayerView; seat: number; size?: number }) {
  const id = view.seats[seat]?.identity;
  const src = id ? `${import.meta.env.BASE_URL}assets/portraits/${id.replace('identity.', '')}.webp` : null;
  const [ok, setOk] = useState(src ? portraitCache.get(src) ?? true : false);
  return (
    <div className="portrait" style={{ width: size, height: size, '--seal': SEAL_COLORS[seat] } as React.CSSProperties}>
      {src && ok ? (
        <img
          src={src}
          alt=""
          onError={() => {
            portraitCache.set(src, false);
            setOk(false);
          }}
        />
      ) : (
        <SeatEmblem seat={seat} size={Math.round(size * 0.86)} />
      )}
    </div>
  );
}

/** "Clerk Ambrose · Mortimer Blotch": a player is always their name first, their Bureaucromancer second. */
function identityName(view: PlayerView, seat: number): string | null {
  const id = view.seats[seat]?.identity;
  return id ? (C.identities.get(id)?.name ?? null) : null;
}

// ─── Top bar ─────────────────────────────────────────────────────────────────

/** What the active player is doing, in a few words (the "who" is shown separately). */
export function promptFor(me: number | null | undefined, view: PlayerView, actions: AvailableActions | null): string {
  const d = actions?.decisions[0];
  if (view.phase === 'ended') return 'The Ledger is closed';
  if (d) {
    switch (d.kind) {
      case 'draft':
        // The draft decision lingers in the presented actions while the opening plays out.
        return view.phase === 'draft' ? 'Choose your Bureaucromancer' : 'The Ministry opens';
      case 'choosePermits':
        return `Choose ${d.count} permit${d.count > 1 ? 's' : ''}`;
      case 'discardActions':
        return `Discard ${d.count} Action card${d.count > 1 ? 's' : ''}`;
      case 'playAction':
        return 'Play Action cards';
      case 'buySpell':
        return 'Buy a spell';
      case 'claimProblem':
        return 'Claim a City Problem';
      case 'chooseLocation':
        return d.purpose === 'audit' ? 'Choose a room to audit' : 'Choose a Department';
      case 'exchange':
        return 'Choose a trade';
      case 'liquidate':
        return 'Liquidate permits';
      case 'swapSpell':
        return 'Swap a spell';
      case 'chooseSpell':
        return d.purpose === 'revoke' ? 'Revoke a rival’s spell' : 'Put a spell Under Review';
      case 'chooseDecree':
        return 'Repeal a Decree';
      case 'chooseRival':
        return 'Subpoena a rival';
      case 'returnPermits':
        return 'Swap permits';
      case 'recoverSpell':
        return 'Take a lost spell';
      case 'shred':
        return 'Shred cards';
      case 'giveAction':
        return 'Send an Action card';
    }
  }
  if (view.pending.some((p) => p.seat !== me)) return 'Choosing…';
  if (actions?.free && view.activeSeat === me) {
    if (actions.canPlace && !view.turn?.basePlaced) return resolutionInForce(view, 'resolution.morning_rush') && !view.firstPlacementDone ? 'Place an Assistant · Morning Rush' : 'Place an Assistant';
    return 'Cast spells or end your turn';
  }
  if (view.phase === 'draft') return 'Waiting for the others…';
  return 'Thinking…';
}

function Countdown({ deadline }: { deadline: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.ceil((deadline - now) / 1000));
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');
  return (
    <span className={`timer ${left <= 10 ? 'urgent' : ''}`} data-tip="Turn timer|When it runs out, a Clerk-bot plays your turn.">
      {left > 3600 ? `${Math.round(left / 3600)} h` : `${mm}:${ss}`}
    </span>
  );
}

export function TopBar({ view, actions, onResetCamera }: { view: PlayerView; actions: AvailableActions | null; onResetCamera: () => void }) {
  const me = useGame((st) => st.you?.seat);
  const last = lastRound(view);
  const deadline = useGame((st) => st.room?.deadline);
  const status = useGame((st) => st.status);
  const mode = useGame((st) => st.mode);
  const [menu, setMenu] = useState<'settings' | 'help' | null>(null);
  const prompt = view.phase === 'action' && view.round === 0 ? 'Setting up the table…' : promptFor(me, view, actions);
  const setup = view.phase === 'action' && view.round === 0; // the opening deal is being played out
  // Presented actions can lag a batch behind the table (they update when the animations finish),
  // so "free" only counts while the presented active seat is actually ours.
  const mine = !setup && !!actions && ((actions.free && view.activeSeat === me) || actions.decisions.length > 0);
  const active = view.seats[view.activeSeat];
  const whose = view.phase === 'draft' ? 'The Draft' : setup ? 'The Ministry' : view.phase === 'ended' ? 'Final Audit' : mine ? 'Your Turn' : `${active?.name ?? '…'}`;
  return (
    <header className="topbar">
      <div className="brand" title="Bureaucromancy">
        <span className="brand-crest">
          <Icon name="quill" size={20} />
        </span>
        <span className="brand-word">Bureaucromancy</span>
      </div>
      <div
        className="round-track"
        data-tip={
          last < view.options.rounds
            ? `Round ${view.round} of ${last}|Early Closing: the Ministry now closes at ${closingHour(last)}. The game ends after round ${last}.`
            : `Round ${view.round} of ${last}|The workday runs from 9 AM to 5 PM. The game ends after round ${last}.`
        }
      >
        <span className="round-label">{view.phase === 'draft' ? 'Draft' : view.round === 0 ? 'Setting up' : `Round ${view.round} / ${last}`}</span>
        <span className="round-pips">
          {Array.from({ length: view.options.rounds }, (_, i) => {
            const state = i + 1 < view.round ? 'past' : i + 1 === view.round && view.phase !== 'draft' ? 'now' : '';
            // Early Closing (P-17): the rounds after the new last one are stamped closed.
            if (i + 1 > last) {
              return (
                <span key={i} className="pip closed" data-tip={`Round ${i + 1}: closed|Early Closing — the Ministry now closes at ${closingHour(last)}.`}>
                  <span className="pip-closed-stamp">✕</span>
                </span>
              );
            }
            // The Afternoon Shift's round (P-12) is marked with a clerk and a "+" (Labour Accord may bring it forward).
            if (i + 1 === (view.internRound ?? view.options.internRound)) {
              return (
                <span
                  key={i}
                  className={`pip shift ${state} ${view.internRound != null && view.options.internRound != null && view.internRound < view.options.internRound ? 'early' : ''}`}
                  data-tip={`Round ${i + 1}: the Afternoon Shift|Every player takes on an Intern, a third Assistant for the rest of the game.${view.internRound != null && view.options.internRound != null && view.internRound < view.options.internRound ? ' Labour Accord brought it forward.' : ''}`}
                >
                  <Icon name="meeple" size={10} />
                  <span className="shift-plus">+</span>
                </span>
              );
            }
            // Rounds when the Ministry issues a Decree of its own.
            if (view.options.decreeRounds?.includes(i + 1)) {
              return (
                <span key={i} className={`pip shift decree ${state}`} data-tip={`Round ${i + 1}: a new Decree|The Ministry issues a Decree from the deck. It has no author, and it replaces the oldest if 4 are already in force.`}>
                  <Icon name="scroll" size={10} />
                </span>
              );
            }
            return <span key={i} className={`pip ${state}`} />;
          })}
        </span>
        <span className="round-hour">{roundHour(view.round)}</span>
      </div>
      <div className={`turn-pill ${mine ? 'mine' : ''}`} style={{ '--seal': SEAL_COLORS[view.activeSeat] } as React.CSSProperties}>
        {view.phase === 'action' && active && <Portrait view={view} seat={view.activeSeat} size={34} />}
        <span className="turn-who">{whose}</span>
        <span className="turn-sep" />
        <span className="turn-what">{prompt}</span>
      </div>
      <div className="topbar-actions">
        {deadline && view.phase !== 'ended' && <Countdown deadline={deadline} />}
        {status !== 'open' && mode === 'online' && <span className="conn-badge">{status === 'reconnecting' ? 'Reconnecting…' : status}</span>}
        <QuickChat />
        <button className="icon-btn" data-tip="Recenter the camera|Or press Home. Right-drag to pan, wheel to zoom." onClick={onResetCamera} aria-label="Recenter camera">
          <Icon name="recenter" />
        </button>
        <button className={`icon-btn ${menu === 'help' ? 'is-on' : ''}`} data-tip="How to play" onClick={() => setMenu((m) => (m === 'help' ? null : 'help'))} aria-label="How to play">
          <Icon name="help" />
        </button>
        <button className={`icon-btn ${menu === 'settings' ? 'is-on' : ''}`} data-tip="Settings" onClick={() => setMenu((m) => (m === 'settings' ? null : 'settings'))} aria-label="Settings">
          <Icon name="gear" />
        </button>
      </div>
      {menu === 'settings' && <SettingsMenu onClose={() => setMenu(null)} />}
      {menu === 'help' && <HelpCard onClose={() => setMenu(null)} />}
    </header>
  );
}

function QuickChat() {
  const [open, setOpen] = useState(false);
  const sendChat = useGame((s) => s.sendChat);
  const seated = useGame((s) => s.you?.seat != null);
  const online = useGame((s) => s.mode === 'online');
  if (!seated || !online) return null;
  return (
    <div className="quick-chat">
      <button className={`icon-btn ${open ? 'is-on' : ''}`} data-tip="Quick chat" onClick={() => setOpen((o) => !o)} aria-label="Quick chat">
        <Icon name="chat" />
      </button>
      {open && (
        <div className="menu frame">
          {QUICK_CHAT.map((p, i) => (
            <button
              key={p}
              onClick={() => {
                sendChat(i);
                setOpen(false);
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Slider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="slider-row">
      <span>{label}</span>
      <input type="range" min={0} max={1} step={0.05} value={value} onChange={(e) => onChange(Number(e.target.value))} onPointerUp={() => audio.play('select')} />
    </label>
  );
}

export function SettingsMenu({ onClose, inGame = true }: { onClose: () => void; inGame?: boolean }) {
  const settings = useGame((s) => s.settings);
  const setSettings = useGame((s) => s.setSettings);
  const leave = useGame((s) => s.leave);
  return (
    <div className="settings-menu frame" role="dialog" aria-label="Settings">
      <h3 className="section-title">Sound</h3>
      <Slider label="Master" value={settings.volume} onChange={(v) => setSettings({ volume: v })} />
      <Slider label="Effects" value={settings.sfxVolume} onChange={(v) => setSettings({ sfxVolume: v })} />
      <Slider label="Ambience" value={settings.ambienceVolume} onChange={(v) => setSettings({ ambienceVolume: v })} />
      <h3 className="section-title">Game</h3>
      <label>
        Opponent speed
        <select value={settings.opponentSpeed} onChange={(e) => setSettings({ opponentSpeed: Number(e.target.value) })}>
          <option value={1}>1×</option>
          <option value={1.5}>1.5×</option>
          <option value={2}>2×</option>
          <option value={4}>4×</option>
        </select>
      </label>
      <label>
        <span>Camera follows the action</span>
        <input type="checkbox" checked={settings.followCamera} onChange={(e) => setSettings({ followCamera: e.target.checked })} />
      </label>
      <label>
        <span>Guided tips</span>
        <input type="checkbox" checked={settings.coach} onChange={(e) => setSettings({ coach: e.target.checked, coachSeen: e.target.checked ? [] : settings.coachSeen })} />
      </label>
      <label>
        <span>Reduced motion</span>
        <input type="checkbox" checked={settings.reducedMotion} onChange={(e) => setSettings({ reducedMotion: e.target.checked })} />
      </label>
      {'wakeLock' in navigator && (
        <label>
          <span>Keep the screen awake</span>
          <input type="checkbox" checked={settings.keepAwake} onChange={(e) => setSettings({ keepAwake: e.target.checked })} />
        </label>
      )}
      <label>
        <span>Show the Ledger (game log)</span>
        <input type="checkbox" checked={settings.showLog} onChange={(e) => setSettings({ showLog: e.target.checked })} />
      </label>
      <div className="row">
        <button onClick={onClose}>Close</button>
        {inGame && (
          <button className="danger" onClick={() => confirm('Leave this game?') && leave()}>
            Leave game
          </button>
        )}
      </div>
    </div>
  );
}

function HelpCard({ onClose }: { onClose: () => void }) {
  return (
    <div className="settings-menu help-card frame" role="dialog" aria-label="How to play">
      <h3 className="section-title">How to play</h3>
      <ol>
        <li>
          <b>Place</b> an Assistant in a lit room and take its effect.
        </li>
        <li>
          <b>Cast</b> spells from your hand by paying their permits. Cast spells score VP and keep working.
        </li>
        <li>
          <b>Claim</b> City Problems at the Grand Auditor’s Office.
        </li>
        <li>
          <b>End your turn.</b> After 8 rounds, the most VP wins.
        </li>
      </ol>
      <p className="hint">
        {TOUCH
          ? 'Tap anything for details; tap a room or card again to act. Press and hold a button to see what it does. Drag to pan, pinch to zoom.'
          : 'Hover anything for details. Right-drag or WASD to pan, wheel to zoom, Home to recenter. Space ends your turn, Z undoes.'}
      </p>
      <div className="row">
        <button onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}

// ─── Your dossier (left) ─────────────────────────────────────────────────────

export function MyPanel({ view, actions }: { view: PlayerView; actions: AvailableActions | null }) {
  const me = useGame((s) => s.you?.seat);
  const command = useGame((s) => s.command);
  const busy = useGame((s) => s.busy || s.inFlight);
  const [filed, setFiled] = useState(false);
  if (me == null || !view.seats[me]) return null;
  const st = view.seats[me]!;
  const identity = st.identity ? C.identities.get(st.identity) : null;
  const idActs = (actions?.activations ?? []).filter((a) => a.ref.source === 'identity');
  const free = !!actions?.free && !busy;
  const regular = st.assistants.filter((a) => !a.temp);
  const temps = st.assistants.filter((a) => a.temp);
  const manaShown = Math.max(6, Math.min(12, st.mana));
  const turn = view.phase === 'action' && view.round > 0 && view.activeSeat === me;
  return (
    <aside className={`my-panel frame ${turn ? 'is-turn' : ''}`} aria-label="Your dossier" style={{ '--seal': SEAL_COLORS[me] } as React.CSSProperties}>
      {turn && <TurnFx key={view.round} />}
      <div className="my-panel-body hud-panel">
        <div className="dossier-head" data-anchor="desk-identity" data-tip={identity ? `${identity.name} · ${identity.role}|${identity.abilityName}: ${identity.text}` : undefined}>
          <Portrait view={view} seat={me} size={72} />
          <div className="dossier-names">
            <div className="dossier-name">{st.name}</div>
            <div className="dossier-identity">{identity?.name ?? 'Unappointed'}</div>
            <div className="dossier-rank" data-tip="term:rank">
              {RANKS[st.rank]}
            </div>
          </div>
        </div>
        <div className="vp-line">
          <div className="stamp-slot" data-anchor="desk-stamp">
            <PrecedenceSeal view={view} seat={me} size={54} ghost />
          </div>
          <div className="vp-row" data-anchor="desk-vp" data-tip="term:VP">
            <span className="vp-badge">VP</span>
            <span className="vp-num">{st.vp}</span>
          </div>
        </div>

        <div className="section-title" data-tip="term:mana">
          Mana
        </div>
        <div className="mana-row" data-anchor="desk-mana" data-tip={`${st.mana} mana|Buys spells at the Arcanium. +1 at the start of every round.`}>
          {Array.from({ length: manaShown }, (_, i) => (
            <ManaCrystal key={i} size={20} empty={i >= st.mana} />
          ))}
          <span className="mana-num">{st.mana}</span>
        </div>

        <div className="section-title" data-tip="term:permit">
          Permits
        </div>
        <div className="permit-grid">
          {COLORS.map((c) => (
            <div key={c} className={`permit-cell ${st.permits[c] ? '' : 'zero'}`} data-anchor={`desk-permit-${c}`} data-tip={`${TOKEN[c]} ${COLOR_NAME[c]} permits: ${st.permits[c]}`}>
              <PermitToken color={c} size={30} />
              <b>{st.permits[c]}</b>
            </div>
          ))}
        </div>

        <div className="section-title" data-tip="term:Assistant">
          Assistants
        </div>
        <div className="assistant-row" data-anchor="desk-assistants" data-tip={`${regular.filter((a) => !a.placed).length} of ${regular.length} available this round`}>
          {regular.map((a) =>
            a.intern ? (
              <span key={a.id} className="intern-assistant" data-tip="term:Intern">
                <MeepleIcon color={SEAL_COLORS[me]!} size={21} empty={a.placed} />
              </span>
            ) : (
              <MeepleIcon key={a.id} color={SEAL_COLORS[me]!} size={26} empty={a.placed} />
            ),
          )}
          {temps.map((a) => (
            <span key={a.id} className="temp-assistant" data-tip="term:temporary Assistant">
              <MeepleIcon color={SEAL_COLORS[me]!} size={22} empty={a.placed} />
            </span>
          ))}
        </div>

        <button className={`section-title as-button ${filed ? 'open' : ''}`} data-anchor="desk-spells" onClick={() => setFiled((f) => !f)} aria-expanded={filed} data-tip="Your cast spells|They keep their abilities for the rest of the game.">
          Spells Cast <span className="count">{st.tableau.length}</span>
        </button>
        {filed && <Tableau view={view} actions={actions} seat={me} />}

        {identity && (
          <div className="ability-box" data-tip={`${identity.name}|${identity.role}`}>
            <div className="ability-name">{identity.abilityName}</div>
            <div className="ability-text">
              <RichText text={identity.text} />
            </div>
            {idActs.map((a) => (
              <button key={a.ref.index} className="gilt-btn small" disabled={!free || !a.legal} data-tip={a.legal ? undefined : errorText(a.error)} onClick={() => command({ t: 'activate', ability: a.ref })}>
                {a.label}
              </button>
            ))}
          </div>
        )}

        <AgendaPanel view={view} seat={me} />
      </div>
    </aside>
  );
}

/**
 * The traced gold frame of whoever's turn it is (.turn-fx in hud.css). Its animation plays once
 * from mounting and then settles, so it is keyed by round: a seat that stays active into the next
 * round gets it again.
 */
function TurnFx() {
  return (
    <span className="turn-fx" aria-hidden>
      <span className="turn-ring">
        <i />
      </span>
    </span>
  );
}

function Tableau({ view, actions, seat }: { view: PlayerView; actions: AvailableActions | null; seat: number }) {
  const command = useGame((s) => s.command);
  const busy = useGame((s) => s.busy || s.inFlight);
  const st = view.seats[seat]!;
  const acts = useMemo(() => new Map((actions?.activations ?? []).filter((a) => a.ref.source !== 'identity').map((a) => [a.ref.source, a])), [actions]);
  return (
    <div className="tableau" aria-label="Your cast spells">
      {st.tableau.map((card) => {
        const act = acts.get(card);
        const def = view.cards[card]!;
        const sp = C.spells.get(def);
        return (
          <div
            key={card}
            className={`filed s-${sp?.school} ${reviewed(view, card) ? 'under-review' : ''}`}
            data-anchor={`card-${card}`}
            data-tip={reviewed(view, card) ? `${sp?.name} · Under Review|Its ability is off until the end of round ${view.suspended[card]}.` : `${sp?.name}|${sp?.text}`}
          >
            <span className="filed-name">{sp?.name}</span>
            {reviewed(view, card) && <span className="review-tag">Under Review</span>}
            {act && (
              <button className="gem" disabled={busy || !actions?.free || !act.legal} data-tip={act.legal ? act.label : errorText(act.error)} onClick={() => command({ t: 'activate', ability: act.ref })}>
                <Icon name="crystal" size={12} />
              </button>
            )}
          </div>
        );
      })}
      {!st.tableau.length && <span className="hint">None yet.</span>}
    </div>
  );
}

// ─── Standings (right) ───────────────────────────────────────────────────────

/**
 * Rivals across the top (in turn order after you) and the Gazette and Ledger on the right. Each
 * rival card shows who they are, where they stand and what they hold, and — one click deep — the
 * spells they have cast with their effects. The rival's name opens the full dossier.
 */
export function Standings({ view }: { view: PlayerView }) {
  const me = useGame((s) => s.you?.seat);
  const [dossier, setDossier] = useState<{ seat: number; anchor: DOMRect } | null>(null);
  const [openSpells, setOpenSpells] = useState<{ seat: number; what: 'spells' | 'hand' } | null>(null);
  const n = view.seats.length;
  const start = me == null ? 0 : me + 1;
  const rivals = Array.from({ length: n }, (_, k) => (start + k) % n).filter((seat) => seat !== me);
  useEffect(() => {
    if (openSpells === null) return;
    const close = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest?.('.rival-spells-panel, .rival-spells-toggle, .rival-hand-toggle')) setOpenSpells(null);
    };
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [openSpells]);
  return (
    <>
      <nav className="rivals-bar" aria-label="Rivals">
        {rivals.map((seat) => (
          <RivalCard
            key={seat}
            view={view}
            seat={seat}
            open={openSpells?.seat === seat ? openSpells.what : false}
            onToggleSpells={(what) => setOpenSpells((o) => (o?.seat === seat && o.what === what ? null : { seat, what }))}
            onDossier={(anchor) => setDossier(dossier?.seat === seat ? null : { seat, anchor })}
          />
        ))}
      </nav>
      <aside className="score-col" data-anchor="side-col" aria-label="Resolutions, Decrees and Ledger">
        <ResolutionsRail view={view} />
        <Gazette view={view} />
        <GameLog />
      </aside>
      {dossier && <DossierPopover view={view} seat={dossier.seat} anchor={dossier.anchor} onClose={() => setDossier(null)} />}
    </>
  );
}

function RivalCard({ view, seat, open, onToggleSpells, onDossier }: { view: PlayerView; seat: number; open: 'spells' | 'hand' | false; onToggleSpells: (what: 'spells' | 'hand') => void; onDossier: (anchor: DOMRect) => void }) {
  const room = useGame((s) => s.room);
  const chat = useGame((s) => s.chat);
  const st = view.seats[seat]!;
  const info = room?.seats[seat];
  const talking = chat && chat.seat === seat && Date.now() - chat.at < 4000;
  const avail = st.assistants.filter((a) => !a.placed && !a.temp).length;
  const total = st.assistants.filter((a) => !a.temp).length;
  const active = seat === view.activeSeat && view.phase === 'action' && view.round > 0;
  const spells = st.tableau.map((card) => ({ card, def: view.cards[card]!, sp: C.spells.get(view.cards[card]!)! }));
  const bySchool = SCHOOLS.map((sc) => ({ sc, n: spells.filter((x) => x.sp.school === sc).length })).filter((x) => x.n);
  const review = spells.filter((x) => reviewed(view, x.card)).length;
  return (
    <div className={`rival-card frame ${active ? 'active is-turn' : ''} ${open ? 'open' : ''}`} data-anchor={`seat-${seat}`} style={{ '--seal': SEAL_COLORS[seat] } as React.CSSProperties}>
      {active && <TurnFx key={view.round} />}
      <button
        className="rival-head"
        onClick={(e) => onDossier((e.currentTarget.closest('.rival-card') as HTMLElement).getBoundingClientRect())}
        aria-label={`${st.name}: ${st.vp} VP. Open dossier.`}
        data-tip={`${st.name}|Click for their full dossier: resources, Bureaucromancer, spells and City Problems.`}
      >
        <span className="rival-portrait">
          <Portrait view={view} seat={seat} size={34} />
          <span className="rival-stamp" data-anchor={`seat-${seat}-stamp`}>
            <PrecedenceSeal view={view} seat={seat} size={30} />
          </span>
        </span>
        <span className="rival-names">
          <span className="rival-name">
            <span className="rival-name-text">{st.name}</span>
          </span>
          <span className="rival-sub">
            {info?.kind === 'bot' && <span className="flag">bot</span>}
            {info && info.kind === 'human' && !info.connected && <span className="flag warn">offline</span>}
            {info?.away && <span className="flag warn">covered</span>}
            <span className="rival-sub-text">{identityName(view, seat) ?? RANKS[st.rank]}</span>
          </span>
        </span>
        <span className="rival-vp" data-anchor={`seat-${seat}-vp`} data-tip="term:VP">
          <span className="rival-vp-coin">VP</span>
          <b>{st.vp}</b>
        </span>
      </button>
      <div className="rival-res">
        <span className="mini-permits" data-anchor={`seat-${seat}-permits`}>
          {COLORS.map((c) => (
            <span key={c} className={`mini-permit ${st.permits[c] ? '' : 'zero'}`} data-tip={`${TOKEN[c]} ${COLOR_NAME[c]} permits`}>
              <PermitToken color={c} size={17} />
              <b>{st.permits[c]}</b>
            </span>
          ))}
        </span>
        <span className="mini-stat" data-anchor={`seat-${seat}-mana`} data-tip="term:mana">
          <ManaCrystal size={13} /> <b>{st.mana}</b>
        </span>
        <span className="mini-stat" data-anchor={`seat-${seat}-hand`} data-tip={`Assistants|${avail} of ${total} available this round · ${st.spellHandCount} spells and ${st.actionHandCount} Action cards in hand`}>
          <MeepleIcon color={SEAL_COLORS[seat]!} size={14} /> <b>{avail}</b>
        </span>
      </div>
      <div className="rival-toggles">
      <button className="rival-spells-toggle" data-anchor={`seat-${seat}-spells`} onClick={() => onToggleSpells('spells')} aria-expanded={open === 'spells'} disabled={!spells.length} data-tip={spells.length ? 'Spells cast|Show them and their effects.' : undefined}>
        <span className="rival-spells-label">Spells</span>
        <b>{spells.length}</b>
        <span className="rival-schools">
          {bySchool.map(({ sc, n }) => (
            <span key={sc} className={`dos-school s-${sc}`}>
              <Icon name={SCHOOL_ICON[sc]} size={11} /> {n}
            </span>
          ))}
        </span>
        {review > 0 && <span className="review-tag small">{review} under review</span>}
        {spells.length > 0 && <span className="rival-chevron">▸</span>}
      </button>
      {view.openHands && (
        <button
          key={`hand-${view.openHands}`}
          className="rival-hand-toggle"
          onClick={() => onToggleSpells('hand')}
          aria-expanded={open === 'hand'}
          data-tip={`Their hand · face up|The Fog Lifts: ${st.spellHandCount} spell${st.spellHandCount === 1 ? '' : 's'} and ${st.actionHandCount} Action card${st.actionHandCount === 1 ? '' : 's'}. Show them.`}
        >
          <Icon name="cards" size={12} />
          <b>{st.spellHandCount + st.actionHandCount}</b>
        </button>
      )}
      </div>
      {open === 'spells' && <RivalSpells view={view} spells={spells} />}
      {open === 'hand' && <RivalHand view={view} seat={seat} />}
      {talking && <div className="speech below">{QUICK_CHAT[chat!.phrase]}</div>}
    </div>
  );
}

/** A rival's cast spells with their effects, dropping down over the board; hover for the full card (and its VP). */
function RivalSpells({ view, spells }: { view: PlayerView; spells: { card: CardId; def: string; sp: NonNullable<ReturnType<typeof C.spells.get>> }[] }) {
  const show = usePreview((s) => s.show);
  const hide = usePreview((s) => s.hide);
  useEffect(() => hide, [hide]);
  return (
    <div className="rival-spells-panel frame" role="list">
      {spells.map(({ card, def, sp }) => (
        <div
          key={card}
          role="listitem"
          className={`dos-row s-${sp.school} ${reviewed(view, card) ? 'under-review' : ''}`}
          onMouseEnter={(e) => show(def, values(def, view), e.currentTarget.getBoundingClientRect())}
          onMouseLeave={hide}
          {...tapPreview(def, values(def, view))}
        >
          <Icon name={SCHOOL_ICON[sp.school]} size={14} />
          <span className="dos-row-name">
            {sp.name}
            {reviewed(view, card) && <span className="review-tag">Under Review</span>}
          </span>
          <span className="dos-row-text">
            <RichText text={sp.text} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** The Fog Lifts (P-17): a rival's hand, face up — spells, then Action cards; hover for the full card. */
function RivalHand({ view, seat }: { view: PlayerView; seat: number }) {
  const show = usePreview((s) => s.show);
  const hide = usePreview((s) => s.hide);
  useEffect(() => hide, [hide]);
  const st = view.seats[seat]!;
  const cards = [...(st.spellHand ?? []), ...(st.actionHand ?? [])].filter((c) => view.cards[c]);
  return (
    <div className="rival-spells-panel rival-hand-panel frame" role="list" aria-label={`${st.name}'s hand`}>
      {cards.length === 0 && <p className="hint">An empty hand.</p>}
      {cards.map((card) => {
        const def = view.cards[card]!;
        const sp = C.spells.get(def);
        const ac = C.actions.get(def);
        return (
          <div key={card} role="listitem" className={`dos-row ${sp ? `s-${sp.school}` : 'action'}`} onMouseEnter={(e) => show(def, sp ? values(def, view) : undefined, e.currentTarget.getBoundingClientRect())} onMouseLeave={hide} {...tapPreview(def)}>
            <Icon name={sp ? SCHOOL_ICON[sp.school] : 'stamp'} size={14} />
            <span className="dos-row-name">{sp?.name ?? ac?.name}</span>
            <span className="dos-row-text">
              <RichText text={sp?.text ?? ac?.text ?? ''} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The Resolutions rail (P-17), beside the Gazette: one brass plaque per Resolution in force (hover
 * for its card and the precise rule), and any claimed this round, marked "next round". Only there
 * once there is one to show.
 */
function ResolutionsRail({ view }: { view: PlayerView }) {
  const show = usePreview((s) => s.show);
  const hide = usePreview((s) => s.hide);
  const inForce = view.resolutions?.inForce ?? [];
  const queued = view.resolutions?.queued ?? [];
  if (!inForce.length && !queued.length) return null;
  const plaque = (r: (typeof inForce)[number], pending: boolean) => {
    const def = C.resolutions.get(r.resolution);
    const room = opensRoom(r.resolution);
    return (
      <div
        key={r.resolution}
        className={`res-plaque ${pending ? 'pending' : ''} spice-${def?.spice}`}
        data-anchor={`resolution-${r.resolution}`}
        style={{ '--seal': SEAL_COLORS[r.by] } as React.CSSProperties}
        onMouseEnter={(e) => show(r.resolution, undefined, e.currentTarget.getBoundingClientRect())}
        onMouseLeave={hide}
        {...tapPreview(r.resolution)}
        data-tip={`${def?.name}${pending ? ' · effective next round' : ''}|${def?.rules ?? ''}`}
      >
        <span className="res-plaque-medal">
          <Icon name={room ? LOCATION_ICON[room] : resolutionIcon(r.resolution)} size={14} />
        </span>
        <span className="res-plaque-name">{def?.name}</span>
        {pending ? <span className="res-plaque-tag">next round</span> : <SeatEmblem seat={r.by} size={16} />}
      </div>
    );
  };
  return (
    <section className="frame hud-panel resolutions-rail" data-anchor="resolutions-rail" aria-label="Resolutions in force">
      <div className="section-title centered" data-tip="term:Resolution">
        Resolutions
      </div>
      {inForce.map((r) => plaque(r, false))}
      {queued.map((r) => plaque(r, true))}
    </section>
  );
}

/** A cast spell is Under Review this round (its ability is off). */
const reviewed = (view: PlayerView, card: CardId) => (view.suspended?.[card] ?? 0) >= view.round;

/**
 * The Ministry Gazette: the Decrees in force. It only appears once there is a Decree to read, and
 * each row previews the full Decree card on hover. The oldest one is marked when the Gazette is full,
 * since the next enactment will supersede it.
 */
function Gazette({ view }: { view: PlayerView }) {
  const show = usePreview((s) => s.show);
  const hide = usePreview((s) => s.hide);
  const decrees = view.decrees ?? [];
  if (!decrees.length) return null;
  const full = decrees.length >= view.options.decreeSlots;
  return (
    <section className="frame hud-panel gazette" data-anchor="gazette" aria-label="Decrees in force">
      <div className="section-title centered" data-tip="term:Gazette">
        Decrees
      </div>
      {decrees.map((d, i) => {
        const def = C.decrees.get(d.decree);
        return (
          <div
            key={d.decree}
            className="decree-row"
            data-anchor={`decree-${d.decree}`}
            style={{ '--seal': d.by === null ? 'var(--gilt)' : SEAL_COLORS[d.by] } as React.CSSProperties}
            onMouseEnter={(e) => show(d.decree, undefined, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={hide}
            {...tapPreview(d.decree)}
          >
            <Icon name="scroll" size={15} />
            <span className="decree-name">{def?.name}</span>
            {full && i === 0 && (
              <span className="decree-oldest" data-tip="Oldest Decree|The next Decree enacted will supersede this one.">
                oldest
              </span>
            )}
            {d.by === null ? (
              <span className="ministry-seal" data-tip="Issued by the Ministry|No author: it scores for nobody. Anyone may Repeal it.">
                <Icon name="quill" size={11} />
              </span>
            ) : (
              <SeatEmblem seat={d.by} size={18} />
            )}
          </div>
        );
      })}
    </section>
  );
}

const SCHOOLS = ['conjuration', 'transmutation', 'evocation', 'divination'] as const;

/**
 * A rival's dossier, organised as three questions: where do they stand (score and where it comes
 * from), what can they do next (resources, hand), and what is their engine (ability, spells by
 * school, City Problems). Rows show a large card on hover.
 */
function DossierPopover({ view, seat, anchor, onClose }: { view: PlayerView; seat: number; anchor: DOMRect; onClose: () => void }) {
  const st = view.seats[seat]!;
  const me = useGame((s) => s.you?.seat);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: anchor.left, top: anchor.bottom + 8 });
  const show = usePreview((s) => s.show);
  const hide = usePreview((s) => s.hide);
  // Drop down under the rival's card, kept on screen.
  useLayoutEffect(() => {
    const w = ref.current?.offsetWidth ?? 380;
    const minLeft = (document.querySelector('.my-panel')?.getBoundingClientRect().right ?? 0) + 12;
    setPos({ left: Math.max(minLeft, Math.min(anchor.left + anchor.width / 2 - w / 2, innerWidth - w - 12)), top: anchor.bottom + 8 });
  }, [anchor, seat]);
  useEffect(() => {
    const close = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest?.('.dossier-popover, .rival-head')) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', esc);
      hide();
    };
  }, [onClose, hide]);

  const identity = st.identity ? C.identities.get(st.identity) : null;
  const spells = st.tableau.map((card) => ({ card, def: view.cards[card]!, sp: C.spells.get(view.cards[card]!)! }));
  const spellVp = spells.reduce((n, x) => n + values(x.def, view).vp, 0);
  const problemVp = st.problems.reduce((n, p) => n + p.vp, 0);
  const otherVp = st.vp - spellVp - problemVp;
  const bySchool = SCHOOLS.map((sc) => ({ sc, n: spells.filter((x) => x.sp.school === sc).length }));
  const regular = st.assistants.filter((a) => !a.temp);
  const hover = (def: string) => (e: React.MouseEvent) => show(def, C.spells.has(def) ? values(def, view) : undefined, (e.currentTarget as HTMLElement).getBoundingClientRect());

  return (
    <div ref={ref} className="dossier-popover frame" style={{ top: pos.top, left: pos.left, maxHeight: `calc(100vh - ${Math.round(pos.top)}px - 16px)`, '--seal': SEAL_COLORS[seat] } as React.CSSProperties} role="dialog" aria-label={`${st.name}'s dossier`}>
      <button className="icon-btn small dossier-close" onClick={onClose} aria-label="Close">
        ✕
      </button>

      {/* 1 · Who and where they stand */}
      <header className="dos-head">
        <Portrait view={view} seat={seat} size={52} />
        <div className="dos-who">
          <div className="dos-name">
            {st.name}
            {seat === me && <span className="you"> · you</span>}
          </div>
          <div className="dos-identity">{identity?.name ?? 'Unappointed'}</div>
          <div className="dos-rank">{RANKS[st.rank]}</div>
        </div>
        <div className="dos-vp" data-tip="term:VP">
          <b>{st.vp}</b>
          <span>VP</span>
        </div>
      </header>
      <div className="dos-vp-split" aria-label="Where their VP comes from">
        <span data-tip="From the printed VP of their cast spells">
          <Icon name="star" size={12} /> Spells <b>{spellVp}</b>
        </span>
        <span data-tip="From claimed City Problems">
          <Icon name="scales" size={12} /> Problems <b>{problemVp}</b>
        </span>
        {otherVp !== 0 && (
          <span data-tip="From abilities and bonuses">
            <Icon name="crystal" size={12} /> Other <b>{otherVp}</b>
          </span>
        )}
      </div>

      {/* 2 · What they can do next */}
      <section className="dos-section">
        <div className="section-title">Resources</div>
        <div className="dos-res">
          {COLORS.map((c) => (
            <span key={c} className={`dos-permit ${st.permits[c] ? '' : 'zero'}`} data-tip={`${TOKEN[c]} ${COLOR_NAME[c]} permits`}>
              <PermitToken color={c} size={24} />
              <b>{st.permits[c]}</b>
            </span>
          ))}
          <span className="dos-stat" data-tip="term:mana">
            <ManaCrystal size={18} /> <b>{st.mana}</b>
          </span>
        </div>
        <div className="dos-res small">
          <span data-tip="term:Assistant">
            <MeepleIcon color={SEAL_COLORS[seat]!} size={16} /> {regular.filter((a) => !a.placed).length}/{regular.length} available
          </span>
          <span data-tip={view.openHands ? 'Cards in hand · face up (The Fog Lifts)' : 'Cards in hand (hidden)'}>
            <Icon name="cards" size={14} /> {st.spellHandCount} spell{st.spellHandCount === 1 ? '' : 's'} · {st.actionHandCount} action{st.actionHandCount === 1 ? '' : 's'}
          </span>
          {seat !== me && st.hasAgenda && (
            <span data-tip="term:Secret Agenda">
              <Icon name="scroll" size={13} /> Agenda sealed
            </span>
          )}
        </div>
      </section>

      {view.openHands && seat !== me && (st.spellHand?.length || st.actionHand?.length) ? (
        <section className="dos-section">
          <div className="section-title">
            In hand <span className="count">{st.spellHandCount + st.actionHandCount}</span>
          </div>
          <ul className="dos-list">
            {[...(st.spellHand ?? []), ...(st.actionHand ?? [])].map((card) => {
              const def = view.cards[card];
              if (!def) return null;
              const sp = C.spells.get(def);
              return (
                <li key={card} className={`dos-row ${sp ? `s-${sp.school}` : 'action'}`} onMouseEnter={hover(def)} onMouseLeave={hide}>
                  <Icon name={sp ? SCHOOL_ICON[sp.school] : 'stamp'} size={14} />
                  <span className="dos-row-name">{defName(def)}</span>
                  {sp && <span className="dos-row-vp">{values(def, view).vp}</span>}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {/* 3 · Their engine */}
      {identity && (
        <section className="dos-section">
          <div className="section-title">Bureaucromancer</div>
          <div className="dos-ability" onMouseEnter={hover(identity.id)} onMouseLeave={hide}>
            <div className="dos-ability-name">
              {identity.name} · <em>{identity.abilityName}</em>
            </div>
            <div className="dos-ability-text">
              <RichText text={identity.text} />
            </div>
          </div>
        </section>
      )}

      <section className="dos-section">
        <div className="section-title">
          Spells cast <span className="count">{spells.length}</span>
        </div>
        <div className="dos-schools">
          {bySchool.map(({ sc, n }) => (
            <span key={sc} className={`dos-school s-${sc} ${n ? '' : 'zero'}`} data-tip={`${n} ${sc[0]!.toUpperCase() + sc.slice(1)} spell${n === 1 ? '' : 's'}|City Problems often ask for spells of one school.`}>
              <Icon name={SCHOOL_ICON[sc]} size={13} /> {n}
            </span>
          ))}
        </div>
        {spells.length === 0 ? (
          <p className="hint">No spells cast yet.</p>
        ) : (
          <ul className="dos-list">
            {spells.map(({ card, def, sp }) => (
              <li key={card} className={`dos-row s-${sp.school} ${reviewed(view, card) ? 'under-review' : ''}`} onMouseEnter={hover(def)} onMouseLeave={hide}>
                <Icon name={SCHOOL_ICON[sp.school]} size={14} />
                <span className="dos-row-name">
                  {sp.name}
                  {reviewed(view, card) && (
                    <span className="review-tag" data-tip={`term:Under Review`}>
                      Under Review
                    </span>
                  )}
                </span>
                <span className="dos-row-vp">{values(def, view).vp}</span>
                <span className="dos-row-text">
                  <RichText text={sp.text} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="dos-section">
        <div className="section-title">
          City Problems <span className="count">{st.problems.length}</span>
        </div>
        {st.problems.length === 0 ? (
          <p className="hint">None claimed yet.</p>
        ) : (
          <ul className="dos-list">
            {st.problems.map((p) => (
              <li key={p.problem} className="dos-row problem" onMouseEnter={hover(p.problem)} onMouseLeave={hide}>
                <Icon name="scales" size={14} />
                <span className="dos-row-name">{C.problems.get(p.problem)?.name}</span>
                <span className="dos-row-vp">{p.vp}</span>
                <span className="dos-row-text">{p.tier === 1 ? 'Claimed first' : 'Claimed second'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Progress towards an agenda, from the seat's public stats (shown only in the tooltip). */
function agendaProgress(view: PlayerView, seat: number, cond: Condition): string {
  const s = { seats: view.seats, cards: view.cards, meta: { options: view.options }, decrees: view.decrees, suspended: view.suspended, round: view.round } as unknown as GameState;
  const parts: string[] = [];
  const walk = (c: Condition) => {
    if ('all' in c) return c.all.forEach(walk);
    const v = statValue(s, C, seat, c.stat);
    if (c.atLeast !== undefined) parts.push(`${Math.min(v, c.atLeast)} / ${c.atLeast}`);
    else if (c.atMost !== undefined) parts.push(`${v} (at most ${c.atMost})`);
  };
  walk(cond);
  return parts.join(' · ');
}

function AgendaPanel({ view, seat }: { view: PlayerView; seat: number }) {
  const agenda = view.seats[seat]?.agenda;
  const ag = agenda ? C.agendas.get(agenda) : null;
  if (!ag) return null;
  return (
    <section className="agenda-panel" data-tip={`Secret Agenda · progress ${agendaProgress(view, seat, ag.condition)}|Hidden from the others. Scored at the end of the game.`}>
      <div className="agenda-head">
        <Icon name="scroll" size={16} />
        <span className="agenda-name">{ag.name}</span>
        <span className="agenda-reward">+{ag.vp}</span>
      </div>
      <div className="agenda-text">{ag.text}</div>
    </section>
  );
}

// ─── Hand tray (bottom) ──────────────────────────────────────────────────────

export function HandTray({ view, actions, discardSel, onDiscardToggle }: { view: PlayerView; actions: AvailableActions | null; discardSel: CardId[]; onDiscardToggle: (c: CardId) => void }) {
  const me = useGame((s) => s.you?.seat);
  const [memoCard, setMemoCard] = useState<CardId | null>(null);
  // A new turn (or a decision) drops the memo selection.
  useEffect(() => setMemoCard(null), [actions?.free, actions?.decisions.length, view.round]);
  const selected = useGame((s) => s.selectedSpell);
  const select = useGame((s) => s.select);
  const command = useGame((s) => s.command);
  const undo = useGame((s) => s.undo);
  const busy = useGame((s) => s.busy || s.inFlight);
  if (me == null || !view.seats[me]) {
    return <footer className="tray spectator">Spectating — hands are hidden.</footer>;
  }
  const st = view.seats[me]!;
  const free = !!actions?.free && !busy;
  const casts = new Map((actions?.casts ?? []).map((c) => [c.card, c]));
  const decision = actions?.decisions[0];
  const discarding = decision?.kind === 'discardActions';
  const playing = decision?.kind === 'playAction';
  // Memos Fly Free (P-17): one Action card per round as a free action, from the hand.
  const memo = actions?.freeActions ?? [];
  const memoLegal = new Set(free && !decision ? memo.filter((f) => f.legal).map((f) => f.card) : []);
  const memoUsed = memo.length > 0 && memo.every((f) => !f.legal);
  const avail = st.assistants.filter((a) => !a.placed).length;
  const canEnd = free && !!actions?.canEndTurn;
  const myTurn = view.phase === 'action' && view.round > 0 && view.activeSeat === me;

  const clickSpell = (card: CardId, ev?: React.MouseEvent) => {
    const e = ev ?? pointer;
    const opt = casts.get(card);
    if (!actions?.free) return hint(xy(e).x, xy(e).y, decision ? 'Finish your current choice first' : 'You can cast spells on your turn', 'info');
    if (!opt?.payments.length) {
      audio.play('error');
      return hint(xy(e).x, xy(e).y, errorText(opt?.error));
    }
    audio.play('select');
    select(selected === card ? null : card);
  };

  const clickAction = (card: CardId, ev?: React.MouseEvent) => {
    const e = ev ?? pointer;
    if (discarding) return onDiscardToggle(card);
    if (playing && decision.cards.includes(card)) return command({ t: 'decide', decision: decision.id, choice: { card } });
    if (memoLegal.has(card)) {
      audio.play('select');
      return setMemoCard((c) => (c === card ? null : card));
    }
    if (memo.length && memoUsed) return hint(xy(e).x, xy(e).y, 'You already sent a free memo this round — play more at the Planning Office', 'info');
    hint(xy(e).x, xy(e).y, memo.length ? 'You can send a free memo on your turn' : 'Action cards are played at the Planning Office', 'info');
  };

  return (
    <footer className="tray" aria-label="Your hand">
      <div className="tray-hands">
        <section className="tray-group spells frame" data-anchor="desk-hand">
          <div className="tray-title" data-tip="term:spell">
            Your Spells
          </div>
          <div className="hand-row">
            {(st.spellHand ?? []).map((card) => (
              <Card
                key={card}
                def={view.cards[card]!}
                view={view}
                anchor={`card-${card}`}
                state={selected === card ? 'selected' : null}
                onClick={(e) => clickSpell(card, e)}
                onDoubleClick={casts.get(card)?.payments.length && free ? () => command({ t: 'cast', card, payment: casts.get(card)!.payments[0]! }) : undefined}
              />
            ))}
            {!st.spellHand?.length && <div className="hint empty-hand">No spells in hand</div>}
          </div>
        </section>

        <section className={`tray-group actions frame ${playing || discarding ? 'is-asking' : ''}`} data-anchor="desk-actions">
          <div className="tray-title" data-tip="term:Action card">
            Your Action Cards
          </div>
          {memo.length > 0 && (
            <span
              className={`memo-badge ${memoUsed ? 'used' : memoLegal.size ? 'ready' : ''}`}
              data-tip={memoUsed ? 'Free memo · sent|Memos Fly Free: one Action card per round, anywhere. Yours is sent for this round.' : 'Free memo|Memos Fly Free: once per round, play 1 Action card from your hand as a free action. Click a lit card.'}
            >
              <Icon name="plane" size={12} /> {memoUsed ? 'Sent' : 'Free memo'}
            </span>
          )}
          <div className="hand-row">
            {(st.actionHand ?? []).map((card) => {
              const on = (playing && decision.cards.includes(card)) || (discarding && decision.cards.includes(card));
              return (
                <Card
                  key={card}
                  def={view.cards[card]!}
                  view={view}
                  anchor={`card-${card}`}
                  state={discardSel.includes(card) || memoCard === card ? 'selected' : on || memoLegal.has(card) ? 'playable' : playing || discarding ? 'dim' : null}
                  onClick={(e) => clickAction(card, e)}
                  onDoubleClick={memoLegal.has(card) ? () => command({ t: 'playAction', card }) : undefined}
                />
              );
            })}
            {!st.actionHand?.length && <div className="hint empty-hand">No Action cards</div>}
          </div>
        </section>
      </div>

      {memoCard && memoLegal.has(memoCard) && (
        <div className="cast-pop memo-pop frame" role="dialog" aria-label="Send a free memo">
          <button className="icon-btn small cast-close" onClick={() => setMemoCard(null)} aria-label="Close">
            ✕
          </button>
          <div className="cast-title">
            <Icon name="plane" size={16} /> Send {C.actions.get(view.cards[memoCard]!)?.name}?
          </div>
          <div className="hint">Memos Fly Free · once per round, anywhere</div>
          <button
            className="gilt-btn primary"
            disabled={busy}
            onClick={() => {
              command({ t: 'playAction', card: memoCard });
              setMemoCard(null);
            }}
          >
            Send memo
          </button>
        </div>
      )}

      <div className="tray-end">
        {myTurn ? (
          <>
            <button
              className={`end-turn ${canEnd ? 'ready' : ''} ${actions?.canSkip ? 'compact' : ''}`}
              disabled={!canEnd}
              onClick={() => command({ t: 'endTurn' })}
              data-tip={actions?.free && !actions.canEndTurn ? `Not yet|${errorText(actions.endTurnError)}` : 'End your turn|Space'}
            >
              <Icon name="hourglass" size={30} />
              <span>End Turn</span>
            </button>
            {actions?.canSkip && <SkipTurn disabled={!free} onSkip={() => command({ t: 'endTurn', skip: true })} />}
          </>
        ) : (
          <div className="waiting-plate" style={{ '--seal': SEAL_COLORS[view.activeSeat] } as React.CSSProperties}>
            {view.phase === 'action' && view.round > 0 && view.seats[view.activeSeat] && <Portrait view={view} seat={view.activeSeat} size={40} />}
            <div className="waiting-text">
              <span className="waiting-label">{view.phase === 'draft' ? 'The Draft' : view.round === 0 ? 'Setting up' : 'Waiting on'}</span>
              <b>{view.phase === 'action' && view.round > 0 ? view.seats[view.activeSeat]?.name : '…'}</b>
            </div>
            {/* Keyed so its settling animation plays again for each player's turn. */}
            <Icon key={`${view.round}:${view.activeSeat}`} name="hourglass" size={22} />
          </div>
        )}
        <div className="tray-sub">
          <span className="avail" data-tip="term:Assistant">
            <MeepleIcon color={SEAL_COLORS[me]!} size={18} /> <b>{avail}</b> left
          </span>
          {myTurn && (
            <button className="undo-btn" disabled={!actions?.canUndo || busy} onClick={undo} data-tip="Undo|Z — take back your last action this turn">
              <Icon name="undo" size={14} /> Undo
            </button>
          )}
        </div>
      </div>
    </footer>
  );
}

/** Skipping forfeits this turn's assistant, so it takes a second click to confirm. */
function SkipTurn({ disabled, onSkip }: { disabled: boolean; onSkip: () => void }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      className={`skip-turn ${armed ? 'armed' : ''}`}
      disabled={disabled}
      onClick={() => {
        if (armed) return onSkip();
        audio.play('select');
        setArmed(true);
      }}
      data-tip="Skip turn|Send this turn's assistant home without visiting a department. It stays out for the round."
    >
      <Icon name="skip" size={14} />
      {armed ? 'Click again to skip' : 'Skip Turn'}
    </button>
  );
}

// ─── Casting ─────────────────────────────────────────────────────────────────

const bagTokens = (bag: Partial<Record<(typeof COLORS)[number], number>> | undefined) =>
  COLORS.map((c) => TOKEN[c]!.repeat(bag?.[c] ?? 0)).join('');

function paymentLabel(p: Payment): string {
  if (p.free) return 'Last Original Form — free';
  const parts: string[] = [];
  if (p.discountOn) parts.push(`Legal Precedent −1 ${COLOR_NAME[p.discountOn]}`);
  if (p.reduceBy && Object.values(p.reduceBy).some(Boolean)) parts.push(`Stamp Pound −${bagTokens(p.reduceBy)}`);
  if (p.yellowAs) parts.push(`Yellow as ${COLOR_NAME[p.yellowAs]}`);
  if (p.manaFor) parts.push(`1 mana for ${COLOR_NAME[p.manaFor]}`);
  if (p.manaPermits && Object.values(p.manaPermits).some(Boolean)) parts.push(`${bagTokens(p.manaPermits)} in mana`);
  return parts.length ? parts.join(' · ') : 'Standard payment';
}

/** Cast confirmation for the selected (castable) spell, floating above the tray. */
export function CastPanel({ view, actions }: { view: PlayerView; actions: AvailableActions | null }) {
  const selected = useGame((s) => s.selectedSpell);
  const me = useGame((s) => s.you?.seat);
  const opt = actions?.casts.find((c) => c.card === selected);
  if (!selected || me == null || actions?.decisions.length || !actions?.free || !opt?.payments.length || !view.seats[me]?.spellHand?.includes(selected)) return null;
  return <CastPopover key={selected} view={view} card={selected} payments={opt.payments} spends={opt.spends} mana={view.seats[me]!.mana} />;
}

function CastPopover({ view, card, payments, spends, mana }: { view: PlayerView; card: CardId; payments: Payment[]; spends?: { permits: Partial<Record<(typeof COLORS)[number], number>>; mana: number }[]; mana: number }) {
  const command = useGame((s) => s.command);
  const select = useGame((s) => s.select);
  const busy = useGame((s) => s.busy || s.inFlight);
  const [pick, setPick] = useState(0);
  // Currency Reform (P-17): permits of the chosen payment paid with 3 mana each, beyond its own.
  const [extra, setExtra] = useState<Partial<Record<(typeof COLORS)[number], number>>>({});
  const reform = resolutionInForce(view, 'resolution.currency_reform');
  const def = view.cards[card]!;
  const v = values(def, view);
  const name = C.spells.get(def)?.name;
  const base = payments[pick]!;
  const spend = spends?.[pick];
  const extraN = COLORS.reduce((n, c) => n + (extra[c] ?? 0), 0);
  const manaNeed = (spend?.mana ?? 0) + extraN * 3;
  const payment: Payment = extraN && !base.free ? { ...base, manaPermits: Object.fromEntries(COLORS.map((c) => [c, (base.manaPermits?.[c] ?? 0) + (extra[c] ?? 0)]).filter(([, n]) => (n as number) > 0)) } : base;
  const step = (c: (typeof COLORS)[number], d: number) =>
    setExtra((x) => {
      const n = Math.max(0, Math.min(spend?.permits[c] ?? 0, (x[c] ?? 0) + d));
      if (d > 0 && manaNeed + 3 > mana) return x;
      return { ...x, [c]: n };
    });
  return (
    <div className="cast-pop frame" role="dialog" aria-label={`Cast ${name}`}>
      <button className="icon-btn small cast-close" onClick={() => select(null)} aria-label="Close">
        ✕
      </button>
      <div className="cast-title">Cast {name}?</div>
      <div className="cast-cost">
        {COLORS.flatMap((c) => Array.from({ length: v.cost[c] ?? 0 }, (_, i) => <PermitToken key={c + i} color={c} size={24} />))}
        <span className="cast-vp">
          → <b>{v.vp} VP</b>
        </span>
      </div>
      {payments.length > 1 && (
        <div className="payments">
          {payments.map((p, i) => (
            <label key={i}>
              <input
                type="radio"
                name="payment"
                checked={pick === i}
                onChange={() => {
                  setPick(i);
                  setExtra({});
                }}
              />{' '}
              <span className="pay-label">{paymentLabel(p)}</span>
              {spends?.[i] && (
                <span className="pay-spend">
                  <RichText text={bagTokens(spends[i]!.permits) || '—'} />
                  {spends[i]!.mana > 0 && (
                    <>
                      {' '}+ {spends[i]!.mana} <Glyph kind="mana" />
                    </>
                  )}
                </span>
              )}
            </label>
          ))}
        </div>
      )}
      {reform && !base.free && spend && Object.values(spend.permits).some(Boolean) && (
        <div className="reform-row" data-tip="Currency Reform|Pay any permit of the cost with 3 mana instead.">
          <span className="reform-label">
            <Icon name="coin" size={13} /> 3 <Glyph kind="mana" /> per permit
          </span>
          {COLORS.filter((c) => (spend.permits[c] ?? 0) > 0).map((c) => (
            <span key={c} className="reform-step">
              <button className="small ghost" onClick={() => step(c, -1)} disabled={!(extra[c] ?? 0)} aria-label={`Fewer ${COLOR_NAME[c]} in mana`}>
                −
              </button>
              <PermitToken color={c} size={20} />
              <b>{extra[c] ?? 0}</b>
              <button className="small ghost" onClick={() => step(c, 1)} disabled={(extra[c] ?? 0) >= (spend.permits[c] ?? 0) || manaNeed + 3 > mana} aria-label={`More ${COLOR_NAME[c]} in mana`}>
                +
              </button>
            </span>
          ))}
        </div>
      )}
      <button className="gilt-btn primary" disabled={busy} onClick={() => command({ t: 'cast', card, payment })}>
        Cast{manaNeed > 0 ? ` · ${manaNeed}` : ''}
        {manaNeed > 0 && <Glyph kind="mana" />}
      </button>
    </div>
  );
}

// ─── Room popup (hover detail) ───────────────────────────────────────────────

/**
 * The Stamp of Precedence in the HUD: a red wax "1st" seal on whoever holds it (P-11), matching the
 * stamp on their bench. It moves the moment someone takes it at the Office; they go first from the
 * next round. `ghost`: an empty impression when you don't hold it (your panel keeps its layout).
 */
function PrecedenceSeal({ view, seat, size, ghost = false }: { view: PlayerView; seat: number; size: number; ghost?: boolean }) {
  if (view.phase === 'draft' || view.round === 0) return null;
  const holder = view.stampHolder ?? view.firstSeat;
  const office = view.options.precedenceOffice;
  if (holder !== seat) {
    if (!ghost) return null;
    const who = view.seats[holder]?.name ?? 'a rival';
    return (
      <span className="wax-seal ghost" style={{ width: size, height: size }} data-tip={`Stamp of Precedence|${who} holds it${office ? '. Take it at the Office of Precedence to go first from next round.' : '.'}`}>
        <WaxSealArt size={size} ghost />
      </span>
    );
  }
  const tip = !office
    ? 'Stamp of Precedence|First player this round. It passes to the left each round.'
    : holder === view.firstSeat
      ? 'Stamp of Precedence|Goes first every round until a rival takes the Stamp at the Office of Precedence.'
      : 'Stamp of Precedence|Took it at the Office this round: goes first from next round.';
  // Keyed by holder: a new holder gets the seal pressed on afresh.
  return (
    <span key={holder} className="wax-seal" style={{ width: size, height: size }} data-tip={tip} aria-label="Holds the Stamp of Precedence">
      <WaxSealArt size={size} />
    </span>
  );
}

export function RoomPopup({ loc, x, y, view, actions }: { loc: LocationId; x: number; y: number; view: PlayerView; actions: AvailableActions | null }) {
  const def = C.locations.get(loc)!;
  const cap = locationCapacity(view, loc);
  const occ = view.board[loc].occupants;
  const opt = actions?.free && !view.turn?.basePlaced ? actions.locations.find((l) => l.location === loc) : null;
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x + 18, top: y + 18 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setPos({ left: x + 18 + w > innerWidth - 280 ? x - w - 18 : x + 18, top: Math.max(66, Math.min(y + 18, innerHeight - h - 230)) });
  }, [x, y]);
  return (
    <div ref={ref} className="room-pop frame" style={pos}>
      <div className="room-pop-head">
        <span className="room-medal">
          <Icon name={LOCATION_ICON[loc]} size={18} />
        </span>
        <div>
          <div className="room-pop-title">{def.name}</div>
          <div className="room-pop-text">
            <RichText text={def.text} />
          </div>
        </div>
      </div>
      <div className="room-pop-meta">
        {Array.from({ length: cap }, (_, i) => {
          const a = occ[i];
          const seat = a ? Number(a.slice(1, a.indexOf('a'))) : -1;
          return <span key={i} className={`slot ${a ? 'taken' : ''}`} style={a ? { background: SEAL_COLORS[seat] } : undefined} />;
        })}
        <span>{occ.length >= cap ? 'Full' : `${cap - occ.length} free`}</span>
      </div>
      {roomNotes(view, loc).map((n) => (
        <div key={n.title} className="room-note">
          <Icon name={n.icon} size={13} />
          <span>
            <b>{n.title}</b> · <RichText text={n.text} />
          </span>
        </div>
      ))}
      {loc === 'lost-property' && view.spellDiscard.length === 0 && <div className="tip-warn">The spell discard is empty: nothing to find yet</div>}
      {view.board[loc].blockedBy.length > 0 && <div className="tip-warn">Audited this round</div>}
      {opt && !opt.legal && <div className="tip-warn">{errorText(opt.error)}</div>}
      {opt?.usesIgnoreCapacity && <div className="tip-good">Harlan’s connections get you in</div>}
      {pointer.touch && opt?.legal && <div className="tip-good">Tap again to send your Assistant</div>}
    </div>
  );
}

/** The Resolutions in force that change a room (P-17), as one-line notes for its popup. */
function roomNotes(view: PlayerView, loc: LocationId): { icon: IconName; title: string; text: string }[] {
  const out: { icon: IconName; title: string; text: string }[] = [];
  const on = (id: string) => resolutionInForce(view, id);
  const dept = C.locations.get(loc)?.keyword === 'department';
  if (on('resolution.morning_rush') && !view.firstPlacementDone) out.push({ icon: 'clock', title: 'Morning Rush', text: 'the first placement this round resolves twice' });
  if (dept && on('resolution.magical_weather')) out.push({ icon: 'coin', title: 'Magical Weather', text: 'the Ministry flips a coin; heads, it resolves again' });
  if (loc === 'evocation' && on('resolution.storm_power')) out.push({ icon: 'bolt', title: 'Storm Power', text: 'also gives 1 {MANA}' });
  if (loc === 'precedence' && on('resolution.ceremonial_stipend')) out.push({ icon: 'star', title: 'Ceremonial Stipend', text: 'also gives 1 {MANA}' });
  if (loc === 'mana-conduit' && on('resolution.night_market')) out.push({ icon: 'lantern', title: 'Night Market', text: 'then you may buy 1 spell from the display' });
  if (loc === 'arcanium' && on('resolution.open_shelves')) out.push({ icon: 'book', title: 'Open Shelves', text: 'refreshing the display is free' });
  if (isAnnexRoom(loc)) {
    const by = view.resolutions?.inForce.find((r) => opensRoom(r.resolution) === loc);
    if (by) out.push({ icon: 'building', title: 'Annex room', text: `opened by ${C.resolutions.get(by.resolution)?.name}` });
  }
  return out;
}

export { Glyph };
